import { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api, ProfileAPI } from '../api';
import { toast } from 'sonner';

export function GitHubCallback() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshUser } = useAuth();
  const callbackProcessed = useRef(false);

  useEffect(() => {
    const handleCallback = async () => {
      if (callbackProcessed.current) {
        return;
      }
      callbackProcessed.current = true;

      try {
        const urlParams = new URLSearchParams(location.search);
        const code = urlParams.get('code');
        const state = urlParams.get('state');
        const error = urlParams.get('error');
        if (!code) {
          throw new Error('No authorization code received');
        }


        if (!state) {
          const response = await api.auth.githubLogin(code);

          if (response.data) {
            localStorage.setItem('token', response.data.accessToken);
            api.auth.setToken(response.data.accessToken);

            await refreshUser();

            toast.success('Successfully logged in with GitHub!');
            navigate('/dashboard');
          } else {
            throw new Error('Failed to authenticate with GitHub');
          }
        }
        else {
          if (error) {
            window.opener?.postMessage({
              type: 'GITHUB_OAUTH_ERROR',
              error: error === 'access_denied' ? 'GitHub access was denied' : 'GitHub authentication failed'
            }, window.location.origin);
            window.close();
            return;
          }

          if (!code) {
            window.opener?.postMessage({
              type: 'GITHUB_OAUTH_ERROR',
              error: 'No authorization code received'
            }, window.location.origin);
            window.close();
            return;
          }

          let stateData = null;
          if (state) {
            try {
              stateData = JSON.parse(atob(state));
            } catch (e) {
            }
          }

          const response = await ProfileAPI.connectGithubOAuth(code);

         
          window.opener?.postMessage({
            type: 'GITHUB_OAUTH_SUCCESS',
            action: stateData?.action,
            returnTo: stateData?.returnTo,
            data: response
          }, window.location.origin);

          window.close();
        }

      } catch (err) {
        setError(err instanceof Error ? err.message : 'Authentication failed');
        toast.error('Failed to authenticate with GitHub. Please try again.');

        callbackProcessed.current = false;

        setTimeout(() => {
          navigate('/login');
        }, 5000);
      } finally {
        setLoading(false);
      }
    };

    handleCallback();
  }, [location.search]); 

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f6f6f6]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Authenticating with GitHub...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f6f6f6]">
        <div className="text-center max-w-md">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Authentication Error</h1>
          <p className="text-muted-foreground mb-4">{error}</p>
          <p className="text-sm text-gray-500">Redirecting to login page...</p>
        </div>
      </div>
    );
  }

  return null;
}